import "./CaseInfo.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { DateTime } from "luxon";
import React, { MouseEventHandler } from "react";
import { useSelector } from "react-redux";

import { Case } from "pwapi";

import { CaseDetailState } from "shared/reducers/caseDetailReducer";
import { FeatureFlags } from "shared/reducers/sysReducer";
import { getDateDiffTimes } from "shared/utils/dates";
import { getUserNameStringWithCredentials } from "shared/utils/userUtils";

import ApplicationState from "store/ApplicationState";

import { validOrderingDoctor } from "./dialogs/utils";
import DocsGrid from "./DocsGrid";
import {
  show_clinic,
  show_physician,
} from "./modals/PhysicianPreferencesModal";
import LinkButton from "../LinkButton";
import PatientSexAge from "../PatientSexAge";

interface CaseInfoProps {
  caseDetail: CaseDetailState;
  slideId?: number;
  setPatientModal: (show: boolean) => void;
  setPreferencesModal: (show: boolean) => void;
}

const AorText = ({
  onClick,
  children,
}: {
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  children: React.ReactNode;
}) => {
  return onClick ? <a onClick={onClick}>{children}</a> : <>{children}</>;
};

export const ClinicianInfo = (
  props: Omit<CaseInfoProps, "setPatientModal">
) => {
  const { caseDetail, setPreferencesModal } = props;
  return (
    <div className="CaseInfo CaseInfo--preferences">
      <p>
        <AorText
          onClick={
            show_physician(caseDetail)
              ? () => setPreferencesModal(true)
              : undefined
          }
        >
          <strong>
            {caseDetail.ordering_doctor &&
              getUserNameStringWithCredentials(
                caseDetail.ordering_doctor,
                true
              )}
          </strong>
          {!validOrderingDoctor(caseDetail.ordering_doctor) && (
            <>
              &nbsp;
              <FontAwesomeIcon
                icon="exclamation-circle"
                color="var(--red)"
                title="Missing Ordering Provider"
              />
            </>
          )}
          {caseDetail.physician_preferences && (
            <span className="info-icon info-icon--preferences"> Ⓟ</span>
          )}
          {validOrderingDoctor(caseDetail.ordering_doctor) && (
            <strong>
              {caseDetail.ordering_doctor?.email && (
                <small>
                  <FontAwesomeIcon icon="envelope" />
                </small>
              )}
              {caseDetail.ordering_doctor?.userprofile?.phone && (
                <small>
                  <FontAwesomeIcon icon="phone" />
                </small>
              )}
            </strong>
          )}
        </AorText>
      </p>
      <p>
        <AorText
          onClick={
            show_clinic(caseDetail)
              ? () => setPreferencesModal(true)
              : undefined
          }
        >
          <strong>{caseDetail.patient?.clinic?.name}</strong>
          {caseDetail.clinic_preferences && (
            <span className="info-icon info-icon--preferences"> Ⓟ</span>
          )}
          <strong>
            {caseDetail.patient.clinic?.email && (
              <small>
                <FontAwesomeIcon icon="envelope" />
              </small>
            )}
            {caseDetail.patient.clinic?.phone && (
              <small>
                <FontAwesomeIcon icon="phone" />
              </small>
            )}
          </strong>
        </AorText>
      </p>
    </div>
  );
};

const CaseInfo = (props: CaseInfoProps) => {
  const {
    caseDetail,
    // eslint-disable-next-line unused-imports/no-unused-vars
    slideId = 0,
    setPreferencesModal,
  } = props;

  const features: FeatureFlags = useSelector(
    ({ sys }: ApplicationState) => sys.features
  );

  const user = useSelector(({ auth }: ApplicationState) => auth.user);

  const getPatientAgeMeasurement =
    caseDetail.patient?.date_of_birth &&
    getDateDiffTimes(
      DateTime.fromISO(caseDetail.patient.date_of_birth),
      DateTime.now(),
      ["years"]
    )?.years;

  const isPediatric =
    features?.checkPediatric &&
    getPatientAgeMeasurement &&
    getPatientAgeMeasurement < 18;

  const isHistorical = caseDetail.type === Case.type.HISTORICAL;

  return (
    <>
      <div className="CaseInfo">
        <h1>
          <strong>{caseDetail.accession}</strong>
          {caseDetail.status && (
            <small data-testid={"status"} className="CaseInfo__status">
              ({caseDetail.status}
              {isHistorical && (
                <>
                  {" "}
                  &mdash; <code>HISTORICAL</code>
                </>
              )}
              )
            </small>
          )}
        </h1>
        <h2 className="CaseInfo__patient">
          <strong
            onClick={() => {
              props.setPatientModal(true);
            }}
          >
            {caseDetail.patient.last_name},&nbsp;
            {caseDetail.patient.first_name}
            <small>
              (
              <PatientSexAge
                patient={caseDetail.patient}
                shouldDisplayDOB={false}
              />
              )
              <LinkButton className="info-icon">
                ⓘ
                {isPediatric && (
                  <span className="info-icon--pediatricPatient"> PED</span>
                )}
              </LinkButton>
              {caseDetail.self_pay && (
                <LinkButton
                  className={classNames("info-icon", {
                    "info-icon--doubleRedSelfPay": features?.doubleRedSelfPay,
                  })}
                >
                  {features?.doubleRedSelfPay ? "Self-Pay" : "$"}
                </LinkButton>
              )}
            </small>
            {(!!caseDetail.patient_matches?.length ||
              !!caseDetail?.copath_patient_matches?.length) && (
              <span className="info-icon info-icon--history">Ⓗ</span>
            )}
          </strong>
        </h2>
      </div>
      {(user?.user_type === "dermpath" || user?.is_superuser) && (
        <ClinicianInfo
          caseDetail={caseDetail}
          setPreferencesModal={setPreferencesModal}
        />
      )}
      {!features?.mrmcStudy &&
        (caseDetail.documents?.length > 0 ||
          caseDetail.accession_prefix?.is_diagnostic) && (
          <DocsGrid caseDetail={caseDetail} />
        )}
    </>
  );
};
export default CaseInfo;
